/**
 * AdminTierDemandFunnel — Phase K.2 admin tile.
 *
 * Two rendering modes:
 *   1. STANDALONE PAGE: mounted at `/admin/tier-demand`. Fetches its
 *      own data on mount. Renders a card + tier rows + totals footer.
 *   2. EMBEDDED CARD: parent (e.g. K.3 Launch Readiness Dashboard)
 *      passes `data` as a prop. Component skips the fetch and just
 *      renders. This lets the dashboard batch its own API calls.
 *
 * Copy discipline: Family H bans apply (no background-check /
 * insured / bonded / vetted / screened / cleared-to-do strings).
 * Dollar amounts only for MRR — no rate-derivable framing.
 */
import React, { useEffect, useState } from "react";
import { Loader2, TrendingUp } from "lucide-react";

import { api, formatApiErrorDetail } from "@/lib/api";

const _ENDPOINT = "/admin/partner-tier-subscriptions/demand-funnel";

function _formatDollars(cents) {
  const dollars = Math.round(cents) / 100;
  return `$${dollars.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function _formatTimestamp(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-US", {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return iso;
  }
}

/* ------------------------------------------------------------------ *
 * Presentational sub-components
 * ------------------------------------------------------------------ */
function TierRow({ row }) {
  return (
    <div
      data-testid={`tier-demand-row-${row.tier_id}`}
      style={{
        display: "grid",
        gridTemplateColumns: "1.4fr 1fr 1fr 1.2fr",
        gap: 12,
        padding: "14px 4px",
        borderTop: "1px solid #e5e7eb",
        alignItems: "center",
      }}
    >
      <div>
        <div
          style={{ fontWeight: 700, fontSize: 15, color: "#374751" }}
          data-testid={`tier-demand-name-${row.tier_id}`}
        >
          {row.tier_name}
        </div>
        <div style={{ fontSize: 12, color: "#6b7280", marginTop: 2 }}>
          {_formatDollars(row.subscription_price_cents)}/mo
        </div>
      </div>
      <div>
        <div
          style={{ fontSize: 22, fontWeight: 800, color: "#374751" }}
          data-testid={`tier-demand-intent-${row.tier_id}`}
        >
          {row.intent_count}
        </div>
        <div style={{ fontSize: 11, color: "#6b7280" }}>intents</div>
      </div>
      <div>
        <div
          style={{ fontSize: 14, fontWeight: 600, color: "#6b7280" }}
          data-testid={`tier-demand-live-${row.tier_id}`}
        >
          {row.admin_promoted_stub_count} live
        </div>
        <div style={{ fontSize: 11, color: "#9ca3af" }}>via stub</div>
      </div>
      <div style={{ textAlign: "right" }}>
        <div
          style={{ fontSize: 16, fontWeight: 800, color: "#065f46" }}
          data-testid={`tier-demand-mrr-${row.tier_id}`}
        >
          {_formatDollars(row.projected_mrr_cents)}
        </div>
        <div style={{ fontSize: 11, color: "#6b7280" }}>projected MRR</div>
      </div>
    </div>
  );
}

function TotalsFooter({ totals }) {
  return (
    <div
      data-testid="tier-demand-totals"
      style={{
        marginTop: 4,
        padding: "16px 4px 4px",
        borderTop: "2px solid #d1d5db",
        display: "grid",
        gridTemplateColumns: "1.4fr 1fr 1fr 1.2fr",
        gap: 12,
        alignItems: "center",
      }}
    >
      <div style={{ fontSize: 13, fontWeight: 700, color: "#374751" }}>
        Totals
      </div>
      <div
        data-testid="tier-demand-total-intents"
        style={{ fontSize: 20, fontWeight: 800, color: "#374751" }}
      >
        {totals.total_intent_count}
      </div>
      <div
        data-testid="tier-demand-total-live"
        style={{ fontSize: 13, color: "#6b7280" }}
      >
        {totals.total_admin_promoted_stub_count} live
      </div>
      <div
        data-testid="tier-demand-total-mrr"
        style={{
          fontSize: 18,
          fontWeight: 800,
          color: "#065f46",
          textAlign: "right",
        }}
      >
        {_formatDollars(totals.total_projected_mrr_cents)}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Main component
 * ------------------------------------------------------------------ */
export function AdminTierDemandFunnelCard({ data, loading, error }) {
  const hasAnyIntent = data && data.totals && data.totals.total_intent_count > 0;

  return (
    <div
      data-testid="tier-demand-funnel-card"
      style={{
        border: "1px solid #e5e7eb",
        borderRadius: 12,
        padding: 20,
        background: "#ffffff",
        maxWidth: 720,
      }}
    >
      <header
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 12,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <TrendingUp size={18} color="#F89623" />
          <h2
            data-testid="tier-demand-heading"
            style={{
              fontSize: 18,
              fontWeight: 800,
              margin: 0,
              color: "#374751",
            }}
          >
            Tier Demand Funnel
          </h2>
        </div>
        {data?.as_of && (
          <span
            data-testid="tier-demand-asof"
            style={{ fontSize: 11, color: "#9ca3af" }}
          >
            as of {_formatTimestamp(data.as_of)}
          </span>
        )}
      </header>

      {loading && (
        <div
          data-testid="tier-demand-loading"
          style={{ padding: 24, textAlign: "center" }}
        >
          <Loader2 className="animate-spin" size={20} />
        </div>
      )}

      {!loading && error && (
        // Silent-fail per prior chip pattern — hide tile on error.
        <div data-testid="tier-demand-error-hidden" style={{ display: "none" }} />
      )}

      {!loading && !error && data && !hasAnyIntent && (
        <div
          data-testid="tier-demand-empty"
          style={{
            padding: 24,
            textAlign: "center",
            color: "#6b7280",
            fontSize: 14,
          }}
        >
          No demand data yet — partners will start showing up here as they
          complete the pricing wizard.
        </div>
      )}

      {!loading && !error && data && hasAnyIntent && (
        <>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1.4fr 1fr 1fr 1.2fr",
              gap: 12,
              padding: "0 4px 8px",
              fontSize: 11,
              textTransform: "uppercase",
              letterSpacing: 0.6,
              color: "#9ca3af",
              fontWeight: 700,
            }}
          >
            <div>Tier</div>
            <div>Intent</div>
            <div>Promoted</div>
            <div style={{ textAlign: "right" }}>Projected MRR</div>
          </div>
          {data.by_tier.map((row) => (
            <TierRow key={row.tier_id} row={row} />
          ))}
          <TotalsFooter totals={data.totals} />
        </>
      )}
    </div>
  );
}

/**
 * Standalone page wrapper — mounted at `/admin/tier-demand`.
 * Handles its own fetch lifecycle. Delegates rendering to the
 * dumb `AdminTierDemandFunnelCard` above.
 */
export default function AdminTierDemandFunnel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errored, setErrored] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await api.get(_ENDPOINT);
        if (!cancelled) {
          setData(res.data);
          setErrored(false);
        }
      } catch (e) {
        if (!cancelled) {
          setErrored(true);
          // Silent-fail — no toast spam. Errors are logged server-side.
          // eslint-disable-next-line no-console
          console.warn(
            "tier-demand-funnel fetch failed:",
            formatApiErrorDetail(e?.response?.data?.detail) || e.message,
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div
      data-testid="tier-demand-funnel-page"
      style={{ padding: "24px 20px", maxWidth: 800 }}
    >
      <AdminTierDemandFunnelCard
        data={data}
        loading={loading}
        error={errored}
      />
    </div>
  );
}
